import React, { useEffect, useMemo, useState } from "react";
import useResponsiveFlags from "../../hooks/useResponsiveFlags";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import axios from "../../api/axios";
import FilterMenuDocker from "./FilterMenuDocker";
import DockerHeadFiles from "./DockerTableHead";
import { Button, Alert } from "@mui/material";
import WrapperContainer from "../../components/utilities/WrapperContainer";

const DockerTable = () => {
  const { isDesktop, isSmallMobileScreen } = useResponsiveFlags();
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("id");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [search, setSearch] = useState("");
  const [dataLoading, setDataLoading] = useState(false);
  const [filters, setFilters] = useState({
    module: null,
    startDate: null,
    endDate: null,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, [filters, page, rowsPerPage]);

  useEffect(() => {
    if (!search) handleSearchButtonClick();
  }, [search]);

  const fetchData = async () => {
    setDataLoading(true);
    setErrorMessage("");
    const params = {
      page: page + 1,
      per_page: rowsPerPage,
    };
    if (order) params.sort_order = order;
    if (orderBy) params.sort_by = orderBy;
    if (filters.module) params.module = filters.module;
    if (filters.startDate) params.date_from = filters.startDate;
    if (filters.endDate) params.date_to = filters.endDate;
    if (search) params.search = search;
    axios
      .get("/api/admin_dashboard/dockersdata", { params })
      .then((response) => {
        const result = response.data;
        setData(result.data);
        setTotalCount(result.total);
        setDataLoading(false);
      })
      .catch((err) => {
        setErrorMessage("Failed to fetch Docker Download Table data.");
        setDataLoading(false);
      });
  };

  const handleFilterChange = (column, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [column]: prevFilters[column] === value ? null : value,
    }));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value.toLowerCase());
  };

  const handleSearchButtonClick = () => {
    if (page != 0) setPage(0);
    else fetchData();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    return [...data].sort(getComparator(order, orderBy));
  }, [data, order, orderBy]);

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a, b, orderBy) {
    let valueA = a[orderBy];
    let valueB = b[orderBy];

    if (orderBy === "regdate") {
      valueA = valueA ? new Date(valueA) : null;
      valueB = valueB ? new Date(valueB) : null;
    }

    if (orderBy === "id" || orderBy === "uid" || orderBy === "runs_count") {
      valueA = Number(valueA);
      valueB = Number(valueB);
    }

    if (typeof valueA === "string") valueA = valueA.toLowerCase();
    if (typeof valueB === "string") valueB = valueB.toLowerCase();

    if (valueA == null) return 1;
    if (valueB == null) return -1;

    if (valueB < valueA) return -1;
    if (valueB > valueA) return 1;
    return 0;
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayName = days[date.getDay()];
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);

    return `${dayName} ${month}/${day}/${year}`;
  };

  return (
    <>
      <WrapperContainer
        title="Docker Downloads"
        loggedin={true}
        content={
          <>
            <div className="d-flex flex-column-reverse flex-xl-row justify-content-between mb-xl-3">
              <div className="d-flex align-items-center">
                <input
                  type="text"
                  className="form-control search-icon"
                  placeholder="Search"
                  value={search}
                  onChange={handleSearchChange}
                />
                <Button
                  size="medium"
                  variant="contained"
                  onClick={handleSearchButtonClick}
                  sx={{ padding: "6px 0", marginLeft: "10px" }}
                >
                  Search
                </Button>
                <FilterMenuDocker
                  handleFilterChange={handleFilterChange}
                  filters={filters}
                />
              </div>
            </div>
            {errorMessage && (
              <Alert
                severity="error"
                onClose={() => setErrorMessage("")}
                sx={{ mb: 2 }}
              >
                {errorMessage}
              </Alert>
            )}

            {successMessage && (
              <Alert
                severity="success"
                onClose={() => setSuccessMessage("")}
                sx={{ mb: 2 }}
              >
                {successMessage}
              </Alert>
            )}

            <TableContainer
              className="table-scroll-visible"
              sx={{
                height: isDesktop
                  ? "calc(100vh - 227px)"
                  : "calc(100vh - 297px)",
                minHeight: isDesktop ? "500px" : "300px",
                overflowX: "auto",
                overflowY: "auto"
              }}
            >
              <Table stickyHeader sx={{ minWidth: 750 }} aria-label="org table">
                <DockerHeadFiles
                  order={order}
                  orderBy={orderBy}
                  onSort={handleSort}
                />
                <TableBody>
                  {dataLoading ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : sortedData.length > 0 ? (
                    sortedData.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell
                          style={
                            !isSmallMobileScreen
                              ? {
                                  position: "sticky",
                                  top: 0,
                                  left: 0,
                                  zIndex: 10,
                                  background: "#fff",
                                }
                              : {}
                          }
                          className="custom-size"
                        >
                          {row.id}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.username || "N/A"}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.userip || "N/A"}
                        </TableCell>
                        <TableCell className="custom-size">
                          {formatDate(row.timestamp)}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.module || "N/A"}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={13} align="center">
                        {search ? "No matching data found." : "No data found"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {!dataLoading && totalCount > 0 && (
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} of ${count} total jobs`
                }
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    alignItems: "center",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    margin: 0,
                  },
                  "& .MuiTablePagination-displayedRows": {
                    margin: 0,
                  },
                }}
              />
            )}
          </>
        }
      />
    </>
  );
};

export default DockerTable;
